<template>
    <div class="quill-editor" ref="editor" v-bind="$attrs"></div>
</template>

<script lang="ts">
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import Quill from "quill";
import Vue from "vue";
import { PropType } from "vue";
import QuillResize from "quill-resize-module";
import { mapState } from "vuex";
import axios from "axios";
import Variables, { VariableTree } from 'quill-variables-module';

// Register modules
Quill.register("modules/resize", QuillResize);

import Image from 'quill/formats/image'

// Custom aligmnents classes from resize module
const ALIGNMENT_CLASS_NAMES = [
    "ql-resize-style-left",
    "ql-resize-style-right",
    "ql-resize-style-center",
    "ql-resize-style-full"
];

// Default attributes supported by the original `Image`
const ATTRIBUTES = ['alt', 'height', 'width'];

class CustomImage extends Image {
    static formats(domNode) {
        const attributeFormats = ATTRIBUTES.reduce(
            (formats, attribute) => {
                if (domNode.hasAttribute(attribute)) {
                    formats[attribute] = domNode.getAttribute(attribute);
                }
                return formats;
            },
            {},
        );

        const alignmentFormats = ALIGNMENT_CLASS_NAMES.reduce(
            (formats, className) => {
                if (domNode.classList.contains(className)) {
                    formats[className] = true;
                }
                return formats;
            },
            {},
        );

        return {
            ...attributeFormats,
            ...alignmentFormats
        }
    }

    format(name, value) {
        if (ATTRIBUTES.indexOf(name) > -1) {
            if (value) {
                this.domNode.setAttribute(name, value);
            } else {
                this.domNode.removeAttribute(name);
            }
        } else if (ALIGNMENT_CLASS_NAMES.indexOf(name) > -1) {
            this.domNode.classList.add(name);
        } else {
            super.format(name, value);
        }
    }

    static create(value) {
        const node = super.create(value);

        // Set default width to 250 if no width is specified
        if (!node.hasAttribute('width')) {
            node.setAttribute('width', '250');
        }

        return node;
    }

    static value(domNode) {
        const value = super.value(domNode);

        // Ensure width is set to 250 if not present
        if (!domNode.hasAttribute('width')) {
            domNode.setAttribute('width', '250');
        }

        return value;
    }
}

Quill.register(CustomImage, true);

// MIME type to file extension mapping
const MIME_TO_EXTENSION: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/bmp': 'bmp',
    'image/tiff': 'tif',
    'image/tif': 'tif',
};

interface ImageUploadTracker {
    originalContent: string;
    fileId: string | null;
    mime: string;
    filetype: string;
}

export default Vue.extend({
    name: "QuillEditor",
    props: {
        value: {
            type: String,
            default: "",
        },
        clinic_id: {
            type: String,
            default: "",
        },
        title: {
            type: String,
            default: "",
        },
        placeholder: {
            type: String,
            default: "",
        },
        variables: {
            type: Object as PropType<VariableTree>,
            default: () => ({}),
        },
    },
    data() {
        return {
            quill: null as Quill | null,
            onChangeHandler: null as ((delta: any, oldDelta: any, source: string) => void) | null,
            images_uploading: false,
            image_loaded_percent: 0,
            uploadTracker: {} as Record<string, ImageUploadTracker>,
            debounceTimer: null as number | null,
            uploadQueue: [] as Promise<any>[],
        };
    },
    mounted() {

        let modules: any = {
            toolbar: [
                ["bold", "italic", "underline", "strike"],
                ["blockquote", "code-block", "link"],
                [{ indent: "-1" }, { indent: "+1" }],
                [{ header: 1 }, { header: 2 }],
                [{ list: "ordered" }, { list: "bullet" }],
                ["clean"],
                ["image"],
            ],
            history: {},
            resize: { modules: ["Resize", "DisplaySize", "Toolbar"] },
        };

        if (this.variables && Object.keys(this.variables).length > 0) {
            modules.variables = {
                placeholder: "Placeholders", // TODO: translate i18n
                variables: this.variables,
            };
        }

        this.quill = new Quill(this.$refs.editor as HTMLElement, {
            theme: "snow",
            placeholder: this.placeholder,
            modules,
        });

        if (this.value && this.quill) {
            this.quill.clipboard.dangerouslyPasteHTML(this.value, "silent");
            // Clear the history after setting initial content to prevent undo from removing it
            this.quill.history.clear();
        }

        this.onChangeHandler = () => {
            const html = this.getHTML();
            const normalized = html === "<p><br></p>" ? "" : html;
            this.updateValue(normalized);

            // Trigger debounced upload check
            this.debouncedUploadCheck();
        };
        this.quill.on("text-change", this.onChangeHandler);
    },
    beforeDestroy() {
        if (this.quill && this.onChangeHandler) {
            this.quill.off("text-change", this.onChangeHandler as any);
        }
        this.quill = null;

        // Clear any pending debounced calls
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    },
    computed: {
        ...mapState(["file_base_url", "current_clinic_id"]),
        file_clinic_id() {
            return this.clinic_id || this.current_clinic_id;
        }
    },
    watch: {
        value(newVal: string) {
            if (!this.quill) return;

            const normIncoming = newVal === "<p><br></p>" ? "" : (newVal || "");
            const normCurrent = this.getHTML() === "<p><br></p>" ? "" : this.getHTML();
            if (normIncoming === normCurrent) return;

            const sel = this.quill.getSelection();
            const delta = this.quill.clipboard.convert({html: normIncoming});

            this.quill.setContents(delta, "silent");
            if (sel) this.quill.setSelection(sel);
        }
    },
    methods: {
        getHTML(): string {
            return this.quill?.getSemanticHTML() || "";
        },
        updateValue(value: string) {
            // Strip leading and trailing whitespace
            let emit_value = value.trim();
            
            // If it's just an empty paragraph, emit an empty string
            if (emit_value === "<p></p>") {
                emit_value = "";
            }

            this.$emit("input", emit_value);
        },
        image_upload_progress(progressEvent: any) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            this.image_loaded_percent = percentCompleted;
        },
        debouncedUploadCheck() {
            // Clear any existing timer
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            // Set new timer for 1 seconds
            this.debounceTimer = window.setTimeout(() => {
                this.checkAndUploadImages();
            }, 1000);
        },
        checkAndUploadImages() {
            this.extract_upload_image();
        },
        extract_upload_image() {
            if (!this.quill) return;

            // Get all image elements in the Quill editor
            const editor = this.$refs.editor as HTMLElement;
            const imageElements = editor.querySelectorAll('.ql-editor img[src^="data:image"]');

            if (imageElements.length === 0) {
                return; // No base64 images to upload
            }

            imageElements.forEach((imgElement) => {
                const img = imgElement as HTMLImageElement;
                const src = img.src;

                // Skip if already being uploaded
                if (this.uploadTracker[src]) {
                    return;
                }

                // Extract MIME type and base64 data
                const match = src.match(/^data:(image\/[^;]+);base64,(.+)$/);
                if (!match) {
                    return;
                }

                const mime = match[1];
                const base64Data = match[2];
                const filetype = MIME_TO_EXTENSION[mime] || mime.split("/")[1] || 'bin';
                const image_binary = atob(base64Data);

                const byteNumbers = new Array(image_binary.length);
                for (let i = 0; i < image_binary.length; i++) {
                    byteNumbers[i] = image_binary.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const image_file = new File([byteArray], "quill_image." + filetype);

                // Track this image upload
                this.uploadTracker[src] = {
                    originalContent: src,
                    fileId: null,
                    mime,
                    filetype,
                };

                // Upload
                const formData = new FormData();
                formData.append("file", image_file);
                formData.append("title", (this.title || "Untitled"));
                formData.append("description", "");
                formData.append("category", "quill_image");
                formData.append("clinic_id", this.file_clinic_id);
                formData.append("filename", "quill_image." + filetype);
                formData.append("mime", mime);
                formData.append("public", "true");

                const promise = axios.post("/api/file/upload", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: this.image_upload_progress,
                }).then(response => {
                    const resp = response.data;
                    if (resp.status === "success") {
                        this.uploadTracker[src].fileId = resp.data;
                        this.replaceImageInDOM(img, src);
                    } else {
                        this.$emit("upload-error", resp);
                        delete this.uploadTracker[src];
                    }
                }).catch(error => {
                    this.$emit("upload-error", error);
                    delete this.uploadTracker[src];
                }).finally(() => {
                    // Remove the promise from the queue when it completes
                    const index = this.uploadQueue.indexOf(promise);
                    if (index > -1) {
                        this.uploadQueue.splice(index, 1);
                    }
                });

                // Add promise to the upload queue
                this.uploadQueue.push(promise);

                // Set uploading state if this is the first upload
                if (this.uploadQueue.length === 1) {
                    this.images_uploading = true;
                }
            });

            // Check if all uploads are complete
            if (this.uploadQueue.length === 0) {
                this.images_uploading = false;
            }
        },
        replaceImageInDOM(imgElement: HTMLImageElement, originalSrc: string) {
            const tracker = this.uploadTracker[originalSrc];
            if (!tracker || !tracker.fileId) {
                return;
            }

            const file_url = this.file_base_url + this.file_clinic_id + "/" + tracker.fileId + "/1.quill_image." + tracker.filetype;

            // Update the image src directly in the DOM
            imgElement.src = file_url;

            // Clean up tracker
            delete this.uploadTracker[originalSrc];

            // Update the v-model
            const html = this.getHTML();
            const normalized = html === "<p><br></p>" ? "" : html;
            this.updateValue(normalized);

            // Emit event
            this.$emit("image-uploaded", tracker.fileId);
        },

        /**
         * Wait for all pending image uploads to complete
         * @returns Promise that resolves when all uploads are complete
         */
        waitForAllUploads(): Promise<void> {
            this.extract_upload_image();
            if (this.uploadQueue.length === 0) {
                return Promise.resolve();
            }
            return Promise.all(this.uploadQueue).then(() => {
                // All uploads completed successfully
            }).catch(() => {
                // Some uploads failed, but we still wait for all to complete
            });
        },

        /**
         * Get the current number of pending uploads
         * @returns Number of uploads currently in progress
         */
        getPendingUploadCount(): number {
            return this.uploadQueue.length;
        },
    },
});
</script>

<style>
.quill-editor {
    resize: vertical;
    overflow: auto;
    border: 1px solid #ccc;
}

.theme--dark .ql-snow .ql-stroke {
    stroke: gray;
}

.theme--light .quill-editor p {
    color: black;
}

/* RTL languages */
html[lang="he"] .quill-editor,
html[lang="ar"] .quill-editor {
    text-align: right;
}

@media (min-width: 1000px) {
    .ql-variables-menu {
        width: 400px;
    }
}

@media (min-width: 1130px) {
    .ql-variables-menu {
        width: 500px;
    }
}
</style>